import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import $ from 'jquery';

import type { Service } from 'webapp-types';

import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import BoardMobileFullscreenComponent from 'webapp/components/board/mobile-fullscreen';
import BoardWallComponent from 'webapp/components/board/wall';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type BoardState from 'webapp/models-custom/board-state';
import type DateRange from 'webapp/models-custom/date-range';
import type DsCompare from 'webapp/models-custom/ds-compare';

interface Args {
	boardState: BoardState;
	index: number;
	isActive: boolean;
	showBottomInfo: boolean;
	isFullscreen: boolean;
	isInIframe?: boolean;
	showDesignerLink?: boolean;
	skipActions?: boolean;
	isPortraitView: boolean;
	onBoardPreviewHashChange: (boardId: string, previewHash?: string | null) => void;
	onToggleFullscreen?: () => void;
	trackingOrigin?: string;
	slideCount: number;
	changeMdr?: (dateRange: DateRange, compare: DsCompare | null) => void;
}

export default class DatawallWallsSlideComponent extends Component<Args> {
	@service('navigation') declare navigationService: Service.Navigation;

	get boardState() {
		return this.args.boardState;
	}

	get board() {
		return this.boardState.boardCM;
	}

	get showFullscreenButton() {
		return this.args.isInIframe && !!this.args.onToggleFullscreen;
	}

	get renderContent() {
		// skip rendering content in general if on snapshooter for perf purposes
		// the actual "not-rendering-blocks-on-not-visible-boards" perf improvement is on board/base (showLoader)
		// we don't move it here bcos we want the board background color visible on slide change immediately
		return !this.navigationService.isLoadedFromSnapshooter || this.args.isActive;
	}

	@action
	onBoardChangesUpdate() {
		// we need this because the "initial" loaded data does not include "changes"
		// which come later in another request
		this.boardState.setPropsFromChanges();
	}

	@action
	cleanupDataOrderAttribute(node: HTMLElement) {
		$(node).attr('data-order', null); // so we can cleanup the node in walls-slider
	}

	@action
	toggleFullscreen() {
		const onToggleFullscreen = this.args.onToggleFullscreen;
		if (onToggleFullscreen) {
			onToggleFullscreen();
		}
	}
	<template>
		<div
			class="slide"
			data-order={{@index}}
			{{didUpdate this.onBoardChangesUpdate this.board.changes}}
			{{willDestroy this.cleanupDataOrderAttribute}}
		>
			<div class="bgTheme-{{this.board.bgTheme}} colorTheme-{{this.board.themeId}}">
				{{#if this.renderContent}}
					{{#if @isFullscreen}}
						<BoardMobileFullscreenComponent
							@boardState={{this.boardState}}
							@isPublicUrl={{true}}
							@isActive={{@isActive}}
							@showBottomInfo={{@showBottomInfo}}
							@skipActions={{@skipActions}}
							@slideCount={{@slideCount}}
						/>
					{{else}}
						<BoardWallComponent
							@boardState={{this.boardState}}
							@isPublicUrl={{true}}
							@isActive={{@isActive}}
							@showBottomInfo={{@showBottomInfo}}
							@isInIframe={{@isInIframe}}
							@showDesignerLink={{@showDesignerLink}}
							@skipActions={{@skipActions}}
							@isPortraitView={{@isPortraitView}}
							@trackingOrigin={{@trackingOrigin}}
							@slideCount={{@slideCount}}
							@changeMdr={{@changeMdr}}
						/>
					{{/if}}

					{{#if this.showFullscreenButton}}
						<a
							role="button"
							class="fullscreenToggle"
							{{on "click" this.toggleFullscreen}}
						>
							<CommonUiIconComponent @name="FullScreen" @size="lg" />
						</a>
					{{/if}}
				{{/if}}
			</div>
		</div>
	</template>
}
